<template>
  <div v-if="availableSupplements.length > 0" class="supplements-selection-section mt-4 mb-4">
    <div class="supplements-grid">
      <div
        v-for="supp in availableSupplements"
        :key="supp.key"
        class="supplement-card"
        :class="{ 'supplement-card--selected': isSelected(supp.key) }"
        @click="toggle(supp)"
      >
        <div class="supplement-card-top">
          <img :src="supp.iconSrc" :alt="supp.label" class="supplement-card-icon" />
          <span
            class="supplement-card-checkbox"
            :class="{ 'supplement-card-checkbox--checked': isSelected(supp.key) }"
            @click.stop="toggle(supp)"
          >
            <svg v-if="isSelected(supp.key)" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 5L4.5 8.5L11 1.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
        <p class="raleway-semi-p14 mb-1">{{ supp.label }}</p>
        <p class="raleway-reg-p13 mb-2" style="color: #707070;">
          <template v-if="supp.selection === 'gratuit'">Gratuit</template>
          <template v-else-if="supp.price">{{ supp.price }} € · {{ supp.info || 'par nuit' }}</template>
          <template v-else>À partir de {{ supp.info }}</template>
        </p>
        <template v-if="isSelected(supp.key)">
          <div class="supplement-qty-row" @click.stop>
            <button class="supplement-qty-btn" @click.stop="decrement(supp.key)" :disabled="getQty(supp.key) <= 1">−</button>
            <span class="supplement-qty-val raleway-semi-p14">{{ getQty(supp.key) }}</span>
            <button class="supplement-qty-btn" @click.stop="increment(supp.key)">+</button>
            <span v-if="supp.qtyLabel" class="raleway-reg-p13 ml-1" style="color:#707070;">{{ supp.qtyLabel }}</span>
          </div>
          <div v-if="requiresSchedule(supp.key)" class="supplement-schedule-fields" @click.stop>
            <p class="raleway-semi-p12 mb-1" style="color:#2C5E4B;">Indiquez la date et l'heure de votre réservation</p>
            <div class="supplement-schedule-inputs">
              <div
                class="supplement-schedule-input-group supplement-date-wrapper"
                :class="{ 'has-value': !!getReservationDate(supp.key) }"
              >
                <input
                  class="supplement-schedule-input"
                  type="date"
                  :value="getReservationDate(supp.key)"
                  @input="updateReservationDate(supp.key, $event.target.value)"
                />
              </div>
              <div class="supplement-schedule-input-group">
                <select
                  class="supplement-schedule-input supplement-schedule-select"
                  placeholder="Heure"
                  :value="getPreferredTimeSlot(supp.key)"
                  @change="updatePreferredTimeSlot(supp.key, $event.target.value)"
                >
                  <option value="" disabled>--:--</option>
                  <option v-for="slot in timeSlots" :key="slot" :value="slot">{{ slot }}</option>
                </select>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="supplement-add-btn">
            <span class="raleway-semi-p13">+ Ajouter</span>
          </div>
        </template>
      </div>
    </div>

    <!-- Selection summary -->
    <div v-if="selectedSupplements.length > 0" class="supplements-summary mt-3">
      <div class="supplements-summary-header">
        <span class="raleway-semi-p13 text-uppercase">Votre sélection</span>
      </div>
      <div class="supplements-summary-rows">
        <div
          v-for="sel in selectedSupplements"
          :key="sel.key"
          class="supplements-summary-row"
        >
          <div class="d-flex align-items-center" style="gap: 8px;">
            <img :src="sel.iconSrc" :alt="sel.label" style="width:18px;height:18px;" />
            <div class="d-flex flex-column">
              <span class="raleway-reg-p14">{{ sel.label }} ×{{ sel.qty }}</span>
              <span
                v-if="sel.reservationDate || sel.preferredTimeSlot"
                class="raleway-reg-p12"
                style="color:#707070;"
              >
                {{ formatSchedule(sel) }}
              </span>
            </div>
          </div>
          <div class="d-flex align-items-center" style="gap: 8px;">
            <span class="raleway-semi-p14">{{ sel.lineTotal > 0 ? sel.lineTotal + ' €' : 'Gratuit' }}</span>
            <button class="supplement-remove-btn" @click.stop="remove(sel.key)">×</button>
          </div>
        </div>
      </div>
      <div class="supplements-summary-total">
        <span class="raleway-semi-p15">Total - Services additionnels</span>
        <span class="raleway-semi-p15">{{ totalDisplay }} €</span>
      </div>
      <p class="raleway-it-l12 mt-1" style="color:#707070;">Ces services additionnels devront être réglés directement sur place.</p>
    </div>
  </div>
</template>

<script>
const SUPPLEMENT_META = [
  { key: 'lit_appoint',     label: "Lit d'appoint",   iconSrc: require('../../images/ic_lit_appoint.svg'),    hasQuantity: false, qtyLabel: '' },
  { key: 'lit_bebe',        label: 'Lit bébé',         iconSrc: require('../../images/ic_lit_bebe.svg'),       hasQuantity: false, qtyLabel: '' },
  { key: 'spa',             label: 'Spa',              iconSrc: require('../../images/ic_spa.svg'),            hasQuantity: false, qtyLabel: '' },
  { key: 'parking',         label: 'Parking',          iconSrc: require('../../images/ic_parking.svg'),        hasQuantity: false, qtyLabel: '' },
  { key: 'animaux',         label: 'Animaux',          iconSrc: require('../../images/ic_animaux.svg'),        hasQuantity: true,  qtyLabel: 'animal(aux)' },
  { key: 'petit_dejeuner',  label: 'Petit déjeuner',   iconSrc: require('../../images/ic_petit_dejeuner.svg'), hasQuantity: true,  qtyLabel: 'pers.' },
  { key: 'dejeuner',        label: 'Déjeuner',         iconSrc: require('../../images/ic_diner.svg'),          hasQuantity: true,  qtyLabel: 'pers.' },
  { key: 'diner',           label: 'Diner',            iconSrc: require('../../images/ic_diner.svg'),          hasQuantity: true,  qtyLabel: 'pers.' },
  { key: 'panier_repas',    label: 'Panier repas',     iconSrc: require('../../images/ic_panier_repas.svg'),   hasQuantity: true,  qtyLabel: 'pers.' },
  { key: 'massages_soins',  label: 'Massages & soins', iconSrc: require('../../images/ic_massages_soins.svg'), hasQuantity: false, qtyLabel: '' },
];

function cloneSupplementEntry(entry = {}) {
  return {
    ...entry,
    selection_options: Array.isArray(entry.selection_options) ? [...entry.selection_options] : entry.selection_options,
  };
}

function normalizeLegacySupplements(raw) {
  const normalized = raw ? { ...raw } : {};
  const legacyMeal = normalized.dejeuner_diner;

  if (legacyMeal) {
    if (!normalized.dejeuner) normalized.dejeuner = cloneSupplementEntry(legacyMeal);
    if (!normalized.diner) normalized.diner = cloneSupplementEntry(legacyMeal);
  }

  return normalized;
}

export default {
  name: 'SupplementsSelector',
  props: {
    lodging: { type: Object, required: true },
    numberOfNights: { type: Number, default: 1 },
    // Two-way binding: parent passes value, component emits 'input' on change
    value: { type: Object, default: () => ({}) },
  },
  computed: {
    timeSlots() {
      const slots = [];
      for (let h = 9; h <= 22; h++) {
        for (let m = 0; m < 60; m += 30) {
          if (h === 22 && m > 0) break;
          const hh = String(h).padStart(2, '0');
          const mm = String(m).padStart(2, '0');
          slots.push(`${hh}:${mm}`);
        }
      }
      return slots;
    },
    availableSupplements() {
      const raw = normalizeLegacySupplements(this.lodging?.lodging_profile?.supplements);
      if (!raw) return [];
      return SUPPLEMENT_META
        .filter(meta => {
          const entry = raw[meta.key];
          return entry && entry.selection && entry.selection !== '';
        })
        .map(meta => ({
          ...meta,
          price: raw[meta.key].price || '',
          info: raw[meta.key].info || '',
          selection: raw[meta.key].selection || '',
        }));
    },
    selectedSupplements() {
      return Object.entries(this.value)
        .filter(([, val]) => val && val.selected)
        .map(([key, val]) => {
          const meta = SUPPLEMENT_META.find(m => m.key === key) || {};
          const raw = normalizeLegacySupplements(this.lodging?.lodging_profile?.supplements)?.[key] || {};
          const priceNum = parseFloat(raw.price) || 0;
          const nights = parseInt(this.numberOfNights) || 1;
          const qty = val.qty || 1;
          const isPerNuit = (raw.info || '').toLowerCase().includes('nuit') || (raw.info || '').toLowerCase().includes('night');
          const lineTotal = raw.selection === 'gratuit' ? 0 : (isPerNuit ? priceNum * nights * qty : priceNum * qty);
          return {
            key,
            label: meta.label || key,
            iconSrc: meta.iconSrc || '',
            qty,
            reservationDate: val.reservationDate || '',
            preferredTimeSlot: val.preferredTimeSlot || '',
            lineTotal: Math.round(lineTotal * 100) / 100,
          };
        });
    },
    total() {
      return this.selectedSupplements.reduce((sum, s) => sum + s.lineTotal, 0);
    },
    totalDisplay() {
      return this.total.toFixed(2);
    },
  },
  methods: {
    isSelected(key) {
      return !!(this.value[key] && this.value[key].selected);
    },
    getQty(key) {
      return (this.value[key] && this.value[key].qty) || 1;
    },
    toggle(supp) {
      const updated = { ...this.value };
      if (updated[supp.key] && updated[supp.key].selected) {
        updated[supp.key] = { selected: false, qty: 1, reservationDate: '', preferredTimeSlot: '' };
      } else {
        updated[supp.key] = { selected: true, qty: 1, reservationDate: '', preferredTimeSlot: '' };
      }
      this.$emit('input', updated);
    },
    increment(key) {
      const updated = { ...this.value };
      const current = updated[key] || { selected: true, qty: 1, reservationDate: '', preferredTimeSlot: '' };
      updated[key] = {
        selected: true,
        qty: (current.qty || 1) + 1,
        reservationDate: current.reservationDate || '',
        preferredTimeSlot: current.preferredTimeSlot || '',
      };
      this.$emit('input', updated);
    },
    decrement(key) {
      const updated = { ...this.value };
      const current = updated[key] || { selected: true, qty: 1, reservationDate: '', preferredTimeSlot: '' };
      updated[key] = {
        selected: true,
        qty: Math.max(1, (current.qty || 1) - 1),
        reservationDate: current.reservationDate || '',
        preferredTimeSlot: current.preferredTimeSlot || '',
      };
      this.$emit('input', updated);
    },
    remove(key) {
      const updated = { ...this.value };
      updated[key] = { selected: false, qty: 1, reservationDate: '', preferredTimeSlot: '' };
      this.$emit('input', updated);
    },
    requiresSchedule(key) {
      return ['spa', 'dejeuner', 'diner', 'massages_soins'].includes(key);
    },
    getReservationDate(key) {
      return (this.value[key] && this.value[key].reservationDate) || '';
    },
    getPreferredTimeSlot(key) {
      return (this.value[key] && this.value[key].preferredTimeSlot) || '';
    },
    updateReservationDate(key, reservationDate) {
      const updated = { ...this.value };
      const current = updated[key] || { selected: true, qty: 1, reservationDate: '', preferredTimeSlot: '' };
      updated[key] = {
        selected: true,
        qty: current.qty || 1,
        reservationDate,
        preferredTimeSlot: current.preferredTimeSlot || '',
      };
      this.$emit('input', updated);
    },
    updatePreferredTimeSlot(key, preferredTimeSlot) {
      const updated = { ...this.value };
      const current = updated[key] || { selected: true, qty: 1, reservationDate: '', preferredTimeSlot: '' };
      updated[key] = {
        selected: true,
        qty: current.qty || 1,
        reservationDate: current.reservationDate || '',
        preferredTimeSlot,
      };
      this.$emit('input', updated);
    },
    formatSchedule(sel) {
      const date = sel.reservationDate || 'date non renseignée';
      const time = sel.preferredTimeSlot || 'heure non renseignée';
      return `${date} · ${time}`;
    },
  },
};
</script>

<style lang="scss" scoped>
$green: #2C5E4B;
$green-light: #F0F7F4;
$border: #E0E0E0;
$text-muted: #707070;
$text-dark: #1a1a1a;

.supplements-selection-section {
  padding-top: 20px;
}

.supplements-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
}

.supplement-card {
  background: #FFFFFF;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 14px 12px 12px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  overflow: hidden;

  &:hover {
    border-color: $green;
    box-shadow: 0 2px 8px rgba(44, 94, 75, 0.1);
  }

  &.supplement-card--selected {
    border-color: $green;
    background: $green-light;
    box-shadow: 0 2px 8px rgba(44, 94, 75, 0.12);
  }
}

.supplement-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 8px;
}

.supplement-card-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.supplement-card-checkbox {
  width: 18px;
  height: 18px;
  border: 1.5px solid #BDBDBD;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s;

  svg {
    width: 12px;
    height: 10px;
    display: block;
  }

  &--checked {
    background: $green;
    border-color: $green;
  }
}

.supplement-add-btn {
  margin-top: 6px;
  width: 100%;
  border: 1px solid $green;
  border-radius: 6px;
  padding: 6px 0;
  text-align: center;
  color: $green;
  font-family: 'Raleway', sans-serif;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.15s;

  &:hover {
    background: $green;
    color: #fff;
  }
}

.supplement-qty-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.supplement-qty-btn {
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  padding: 0;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  color: $green;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: background 0.2s, color 0.2s;

  &:hover:not(:disabled) {
    background: $green;
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.supplement-qty-val {
  min-width: 20px;
  text-align: center;
  color: $text-dark;
}

.supplement-schedule-fields {
  margin-top: 8px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.supplement-schedule-inputs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  overflow: hidden;
}

.supplement-schedule-input-group {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.supplement-date-wrapper {
  position: relative;

  // Visible calendar icon shown at the right (native indicator is made transparent & full-width)
  &::after {
    content: '';
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    pointer-events: none;
    z-index: 1;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23707070' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'/%3E%3Cline x1='16' y1='2' x2='16' y2='6'/%3E%3Cline x1='8' y1='2' x2='8' y2='6'/%3E%3Cline x1='3' y1='10' x2='21' y2='10'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
  }

  // Fake placeholder overlay for iOS Safari (ignores placeholder on type="date")
  &:not(.has-value)::before {
    content: 'jj/mm/aaaa';
    position: absolute;
    left: 7px;
    right: 30px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: $text-muted;
    pointer-events: none;
    z-index: 2;
    font-family: 'Raleway', sans-serif;
    background: #fff;
    line-height: 1;

    @media screen and (max-width: 480px) {
      font-size: 11px;
      left: 5px;
      right: 26px;
    }
  }

  // When empty, make the native date edit fields invisible so our fake placeholder shows cleanly
  &:not(.has-value) .supplement-schedule-input {
    &::-webkit-datetime-edit,
    &::-webkit-datetime-edit-fields-wrapper,
    &::-webkit-datetime-edit-text,
    &::-webkit-datetime-edit-month-field,
    &::-webkit-datetime-edit-day-field,
    &::-webkit-datetime-edit-year-field {
      color: transparent;
    }
  }
}

.supplement-schedule-label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: $text-muted;
}

.supplement-schedule-input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: 34px;
  line-height: 34px;
  border: 1px solid #D9D9D9;
  border-radius: 6px;
  padding: 0 28px 0 6px; // right padding to ensure text doesn't overlap calendar icon
  font-size: 12px;
  color: $text-muted;
  background: #fff;
  -webkit-appearance: none;
  appearance: none;
  display: flex;
  align-items: center;
  outline: none;
  vertical-align: middle;
  position: relative;

  &:focus {
    outline: none;
    box-shadow: none;
    border-color: #D9D9D9;
  }

  &::-webkit-datetime-edit {
    padding: 0;
    min-width: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    height: 100%;
    line-height: normal;
  }

  &::-webkit-datetime-edit-fields-wrapper {
    padding: 0;
    min-width: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
  }

  &::-webkit-datetime-edit-text,
  &::-webkit-datetime-edit-month-field,
  &::-webkit-datetime-edit-day-field,
  &::-webkit-datetime-edit-year-field,
  &::-webkit-datetime-edit-hour-field,
  &::-webkit-datetime-edit-minute-field {
    color: $text-muted;
    min-width: 0;
    padding: 0;
  }

  &[type='date'] {
    text-align: left;
  }

  &[type='date']::-webkit-date-and-time-value {
    text-align: left;
  }

  &::-webkit-calendar-picker-indicator {
    // Expand the indicator to cover the entire input area so clicking anywhere opens the picker
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    opacity: 0; // hide the default icon (we show our own via ::after on the wrapper)
    cursor: pointer;
    z-index: 3;
  }

  @media screen and (max-width: 480px) {
    font-size: 11px;
    padding: 0 4px;
    height: 32px;
    line-height: 32px;
  }
}

.supplement-schedule-select {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23707070' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 6px center;
  padding-right: 22px;
  outline: none;

  &:focus {
    outline: none;
    box-shadow: none;
    border-color: #D9D9D9;
  }

  @media screen and (max-width: 480px) {
    padding-right: 18px;
    background-position: right 4px center;
  }
}

.supplements-summary {
  background: #F7F7F7;
  border-radius: 10px;
  padding: 14px 16px;
  border: 1px solid $border;
}

.supplements-summary-header {
  padding-bottom: 10px;
  border-bottom: 1px solid $border;
  margin-bottom: 10px;
  color: $text-dark;
}

.supplements-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #EBEBEB;

  &:last-child {
    border-bottom: none;
  }
}

.supplement-remove-btn {
  background: none;
  border: none;
  color: #999;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  transition: color 0.15s;

  &:hover {
    color: #D56A70;
  }
}

.supplements-summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  margin-top: 6px;
  border-top: 1px solid #BFBFBF;
  color: $text-dark;
}
</style>
